import { useState, useEffect, useRef, useCallback } from "react";
import {
  Bell,
  CheckCircle,
  AlertTriangle,
  XCircle,
  CreditCard,
  Calendar,
  Users,
  Settings,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { formatDistanceToNow } from "date-fns";

interface Notification {
  id: string;
  user_id: string;
  title: string;
  message: string;
  type: string;
  read: boolean;
  metadata: Record<string, unknown>;
  created_at: string;
}

interface NotificationsDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  onUnreadCountChange: (count: number) => void;
}

function getNotificationIcon(type: string) {
  switch (type) {
    case "success":
      return <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />;
    case "warning":
      return <AlertTriangle className="w-4 h-4 text-yellow-400 flex-shrink-0" />;
    case "error":
      return <XCircle className="w-4 h-4 text-red-400 flex-shrink-0" />;
    case "billing":
      return <CreditCard className="w-4 h-4 text-emerald-400 flex-shrink-0" />;
    case "demo":
      return <Calendar className="w-4 h-4 text-blue-400 flex-shrink-0" />;
    case "client":
      return <Users className="w-4 h-4 text-purple-400 flex-shrink-0" />;
    case "system":
      return <Settings className="w-4 h-4 text-gray-400 flex-shrink-0" />;
    case "info":
    default:
      return <Bell className="w-4 h-4 text-secondary flex-shrink-0" />;
  }
}

export function NotificationsDropdown({ isOpen, onClose, onUnreadCountChange }: NotificationsDropdownProps) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Propagate unread count to parent
  useEffect(() => {
    onUnreadCountChange(unreadCount);
  }, [unreadCount, onUnreadCountChange]);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        // Check if clicking on the bell button (parent handles toggle)
        const target = e.target as HTMLElement;
        if (target.closest("[data-notif-trigger]")) return;
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose]);

  // Fetch notifications and set up Realtime subscription
  useEffect(() => {
    let channel: ReturnType<typeof supabase.channel> | null = null;

    const init = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setLoading(false);
        return;
      }

      setUserId(user.id);

      // Fetch recent notifications
      const { data, error } = await supabase
        .from("notifications")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(20);

      if (error) {
        console.error("Failed to fetch notifications:", error);
      } else if (data) {
        setNotifications(data as Notification[]);
        setUnreadCount(data.filter((n: any) => !n.read).length);
      }

      setLoading(false);

      // Subscribe to Realtime for new notifications
      channel = supabase
        .channel("notifications-realtime")
        .on(
          "postgres_changes",
          {
            event: "INSERT",
            schema: "public",
            table: "notifications",
            filter: `user_id=eq.${user.id}`,
          },
          (payload) => {
            const newNotif = payload.new as Notification;
            setNotifications((prev) => [newNotif, ...prev]);
            setUnreadCount((prev) => prev + 1);
          }
        )
        .subscribe();
    };

    init();

    return () => {
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }, []);

  const markAsRead = useCallback(async (notifId: string) => {
    const { error } = await supabase
      .from("notifications")
      .update({ read: true })
      .eq("id", notifId);

    if (!error) {
      setNotifications((prev) =>
        prev.map((n) => (n.id === notifId ? { ...n, read: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    }
  }, []);

  const markAllRead = useCallback(async () => {
    if (!userId) return;

    const { error } = await supabase
      .from("notifications")
      .update({ read: true })
      .eq("user_id", userId)
      .eq("read", false);

    if (!error) {
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    }
  }, [userId]);

  return (
    <div
      ref={dropdownRef}
      className="absolute right-0 top-full mt-1 w-80 bg-surface border border-border rounded-lg shadow-xl z-50"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <h3 className="text-sm font-semibold">Notifications</h3>
        {unreadCount > 0 && (
          <button
            onClick={markAllRead}
            className="text-xs text-secondary hover:text-secondary/80 transition-colors"
          >
            Mark all read
          </button>
        )}
      </div>

      {/* Notification list */}
      <div className="max-h-96 overflow-y-auto">
        {loading ? (
          <div className="p-4 text-center text-sm text-muted-foreground">
            Loading...
          </div>
        ) : notifications.length === 0 ? (
          <div className="p-8 text-center">
            <Bell className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No notifications yet</p>
          </div>
        ) : (
          <div className="py-1">
            {notifications.map((notif) => (
              <button
                key={notif.id}
                onClick={() => {
                  if (!notif.read) markAsRead(notif.id);
                }}
                className="w-full flex items-start gap-3 px-4 py-3 hover:bg-accent transition-colors text-left"
              >
                {/* Unread indicator */}
                <div className="mt-1 flex-shrink-0">
                  {!notif.read ? (
                    <div className="w-2 h-2 rounded-full bg-secondary" />
                  ) : (
                    <div className="w-2 h-2" />
                  )}
                </div>

                {/* Icon */}
                <div className="mt-0.5">
                  {getNotificationIcon(notif.type)}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{notif.title}</p>
                  <p className="text-xs text-muted-foreground line-clamp-2">
                    {notif.message}
                  </p>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    {formatDistanceToNow(new Date(notif.created_at), { addSuffix: true })}
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
